"use client";
import { giftCardItem } from "@/utils/giftCardUtils";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { ItemMarkersSummaryChipContainer } from "@/components/itemMarkersChip/itemMarkersChip";
import { SingleProductDescriptionFocusedPrice } from "./SingleProductDescriptionFocusedPrice";
import { SingleProductDescriptionFocusedDuration } from "./singleProductionDescriptionFocusedDuration";
import { Markers } from "@/dto/marker";
import { Item, Layout } from "@/dto/item";
import { ImageType } from "@/dto/image";


export interface SingleProductProps {
  readonly product: Item;
  readonly merchantName: string;
  readonly markers?: Markers;
  readonly cardChildren?: React.ReactNode;
  readonly layout?: Layout;
}

export const SingleProductDescriptionFocused = ({
  product,
  merchantName,
  markers,
  cardChildren,
  layout = "default",
}: SingleProductProps) => {
  const t = useTranslations("SingleProductDescriptionFocused");
  const markerIds = product.markerIds ?? [];
  const isGiftCardProduct = product.id === giftCardItem.id;
  const productImage =
    product.images && product.images.length > 0
      ? product.images.reduce((prev: ImageType, current: ImageType) => {
          if (
            current.name.includes("xxxhdpi") ||
            (current.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi")) ||
            (current.name.includes("xhdpi") &&
              !prev.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi"))
          ) {
            return current;
          }
          return prev;
        }, product.images[0])
      : null;

  const giftCardDescription = t("giftCardDescription", { merchantName });
  const fullDescription = !isGiftCardProduct
    ? product.description
    : giftCardDescription;

  const imageSize = layout === "enhanced" ? 112 : 120;

  // Container class based on variant
  const getContainerClassName = (): string => {
    switch (layout) {
      case "enhanced":
        return "flex relative h-[138px] rounded-xl cursor-pointer bg-white border border-solid border-[#E5E5E5] p-3";
      default:
        return "flex relative h-[120px] shadow-md rounded-b-md cursor-pointer bg-white";
    }
  };

  // Content wrapper class based on variant
  const getContentWrapperClassName = (): string => {
    switch (layout) {
      case "enhanced":
        return "w-full flex flex-col justify-between flex-grow min-w-0";
      default:
        return "w-full flex flex-col justify-between px-3 pt-4 pb-3 flex-grow min-w-0";
    }
  };

  // Header layout class based on variant
  const getHeaderClassName = (): string => {
    switch (layout) {
      case "enhanced":
        return "flex flex-col";
      default:
        return "flex justify-between";
    }
  };

  const getImageWrapperClassName = (): string => {
    return layout === "enhanced"
      ? "flex-shrink-0 relative w-[112px] h-[112px]"
      : "flex-shrink-0 relative w-[120px] h-[120px]";
  };

  const getItemNameClassName = (): string => {
    if (layout === "enhanced")
      return "line-clamp-1 text-base font-medium text-ellipsis";
    return "line-clamp-1 text-lg font-medium text-ellipsis";
  };

  const getDescriptionClassName = (): string => {
    if (layout === "enhanced")
      return "line-clamp-2 text-sm text-[#5A5A5A] text-ellipsis";
    return "line-clamp-2 text-[15px] m-0 text-ellipsis";
  };

  return (
    <div
      key={product.id}
      data-testid={applyDataTestId(pom.products.singleProduct)}
      className={getContainerClassName()}
    >
      <div className={getContentWrapperClassName()}>
        <ItemMarkersSummaryChipContainer markerIds={markerIds} markers={markers} />
        <div className={getHeaderClassName()}>
          <h3 className="flex flex-col sm:flex-row overflow-hidden">
            <span
              data-testid={applyDataTestId(pom.general.itemName)}
              className={getItemNameClassName()}
            >
              {product.name}
            </span>
            {product.serviceItem && !isGiftCardProduct && (
              <SingleProductDescriptionFocusedDuration
                // @ts-expect-error: serviceItem is typed as an array but used as an object.
                // Its current usage should be reviewed and the type definition corrected if necessary.
                duration={product?.serviceItem?.duration}
              />
            )}
          </h3>

          <div className="m-0">
            <SingleProductDescriptionFocusedPrice
              product={product}
              isGiftCardProduct={isGiftCardProduct}
              layout={layout}
            />
          </div>
        </div>
        <p
          data-testid={applyDataTestId(pom.products.productDescription)}
          className={getDescriptionClassName()}
          title={fullDescription}
        >
          {fullDescription}
        </p>
      </div>

      {productImage && (
        <div className={getImageWrapperClassName()}>
          <div
            className={`absolute top-0 left-0 w-full h-full transition-opacity duration-300`}
          >
            <Image
              data-testid={applyDataTestId(pom.landingPage.productCardImage)}
              width={imageSize}
              height={imageSize}
              sizes="100vw"
              alt={t("productImageAlt")}
              src={productImage.source}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full object-cover"
            />
          </div>
        </div>
      )}
      {cardChildren}
      {!product.available && (
        <div className="absolute h-full w-full inset-0 bg-white opacity-50 rounded-md"></div>
      )}
    </div>
  );
};
